import React, { useState } from 'react';
import { Button } from '../../../ui/button';

const RequestsTab = ({ caseId, requests, onRefresh, token }) => {
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ title: '', message: '', required_upload_slots: [], required_documents: [] });
  const [loading, setLoading] = useState(false);

  const createRequest = async () => {
    if (!form.title.trim() || !form.message.trim()) {
      alert('Titel und Nachricht erforderlich');
      return;
    }
    setLoading(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cases/${caseId}/requests`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      setForm({ title: '', message: '', required_upload_slots: [], required_documents: [] });
      setShowModal(false);
      onRefresh();
      alert('Rückfrage erstellt');
    } catch (error) {
      alert('Fehler');
    } finally {
      setLoading(false);
    }
  };

  const closeRequest = async (requestId) => {
    if (!confirm('Schließen?')) return;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cases/${caseId}/requests/${requestId}/close`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      onRefresh();
      alert('Geschlossen');
    } catch (error) {
      alert('Fehler');
    }
  };

  const toggleSlot = (slot) => {
    setForm(p => ({
      ...p,
      required_upload_slots: p.required_upload_slots.includes(slot)
        ? p.required_upload_slots.filter(s => s !== slot)
        : [...p.required_upload_slots, slot]
    }));
  };

  const toggleDoc = (doc) => {
    setForm(p => ({
      ...p,
      required_documents: p.required_documents.includes(doc)
        ? p.required_documents.filter(d => d !== doc)
        : [...p.required_documents, doc]
    }));
  };

  const statusColors = {
    open: 'bg-orange-100 text-orange-700',
    answered: 'bg-blue-100 text-blue-700',
    closed: 'bg-gray-100 text-gray-700'
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between">
        <h3 className="text-lg font-bold">Rückfragen</h3>
        <Button onClick={() => setShowModal(true)} className="bg-orange-600 hover:bg-orange-700 text-white">+ Erstellen</Button>
      </div>

      {requests.length === 0 ? (
        <p className="text-center text-gray-500 py-8">Keine Rückfragen</p>
      ) : (
        requests.map(req => (
          <div key={req.request_id} className="border border-gray-200 rounded-xl p-6">
            <div className="flex justify-between mb-4">
              <div>
                <h4 className="font-bold text-gray-900 mb-2">{req.title}</h4>
                <p className="text-sm text-gray-600">{req.message}</p>
                <div className="flex gap-4 text-xs text-gray-500 mt-2">
                  <span>Erstellt: {new Date(req.created_at).toLocaleDateString('de-DE')}</span>
                  {req.answered_at && <span>Beantwortet: {new Date(req.answered_at).toLocaleDateString('de-DE')}</span>}
                </div>
              </div>
              <span className={`px-3 py-1 h-8 rounded-full text-xs font-semibold ${statusColors[req.status]}`}>
                {req.status === 'open' ? 'Offen' : req.status === 'answered' ? 'Beantwortet' : 'Geschlossen'}
              </span>
            </div>
            {req.answer_message && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                <p className="text-xs font-semibold text-green-800">Antwort:</p>
                <p className="text-sm text-green-700">{req.answer_message}</p>
              </div>
            )}
            {req.status === 'open' && (
              <Button onClick={() => closeRequest(req.request_id)} variant="outline" size="sm">Schließen</Button>
            )}
          </div>
        ))
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8">
            <h2 className="text-2xl font-bold mb-6">Rückfrage erstellen</h2>
            <div className="space-y-4">
              <input
                type="text"
                value={form.title}
                onChange={e => setForm({...form, title: e.target.value})}
                placeholder="Titel"
                className="w-full px-4 py-3 border rounded-xl"
              />
              <textarea
                value={form.message}
                onChange={e => setForm({...form, message: e.target.value})}
                placeholder="Nachricht"
                rows={4}
                className="w-full px-4 py-3 border rounded-xl"
              />
              <div>
                <p className="text-sm font-semibold mb-2">Bild-Slots (optional)</p>
                <div className="grid grid-cols-2 gap-2">
                  {['damage_detail', 'closeup', 'front_left', 'front_right'].map(slot => (
                    <label key={slot} className="flex items-center gap-2 p-2 border rounded cursor-pointer">
                      <input type="checkbox" checked={form.required_upload_slots.includes(slot)} onChange={() => toggleSlot(slot)} />
                      <span className="text-sm">{slot}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold mb-2">Dokumente (optional)</p>
                <div className="grid grid-cols-2 gap-2">
                  {['registration', 'invoice', 'insurance'].map(doc => (
                    <label key={doc} className="flex items-center gap-2 p-2 border rounded cursor-pointer">
                      <input type="checkbox" checked={form.required_documents.includes(doc)} onChange={() => toggleDoc(doc)} />
                      <span className="text-sm">{doc}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button onClick={createRequest} disabled={loading} className="flex-1 bg-brand-green text-white">
                  {loading ? 'Senden...' : 'Senden'}
                </Button>
                <Button onClick={() => setShowModal(false)} variant="outline" className="flex-1">Abbrechen</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestsTab;
